// @flow
import { get, isEmpty, has } from 'lodash';
import moment from 'moment';
import { messageFormat } from '@clubmed/utils';

import { momentFromApiDate, momentFromApiTime } from 'Utils/date/apiDate';
import { isCharterFlight } from 'Utils/proposal/proposalUtils';
import { ICONS } from 'Components/utils/icon/icons';

export const TRAIN = 'TRAIN';
export const BOAT = 'BOAT';
export const PLANE = 'PLANE';

function getDiffAbsoluteDays(fromDate: string, toDate: string): ?number {
  const fromMoment = momentFromApiDate(fromDate);
  const toMoment = momentFromApiDate(toDate);

  if (fromMoment.isValid() && toMoment.isValid()) {
    return toMoment.diff(fromMoment, 'days');
  }
}

function formatTime(time: string, format: string): ?string {
  const renderedTime = momentFromApiTime(time).format(format);

  return renderedTime !== 'Invalid date' ? renderedTime : null;
}

function getWayLabel(i18n: CMSContent, way: string) {
  if (way === 'INBOUND') return i18n.common.transport.inboundLabel;
  if (way === 'OUTBOUND') return i18n.common.transport.outboundLabel;

  return null;
}

function getMinBaggageWeight(sections: ?TravelSections): { weight?: string, quantity?: number } {
  if (isEmpty(sections)) {
    return {};
  }
  // $FlowFixMe
  const minBaggageWeightForSection = sections.reduce((a, b) => {
    if (!has(a, 'transport.baggage.weight')) {
      return b;
    }
    if (!has(b, 'transport.baggage.weight')) {
      return a;
    }
    const baggageWeightA = a.transport.baggage.weight
      ? Number(a.transport.baggage.weight.replace(/(\d+).*/, '$1'))
      : NaN;
    const baggageWeightB = b.transport.baggage.weight
      ? Number(b.transport.baggage.weight.replace(/(\d+).*/, '$1'))
      : NaN;

    if (!baggageWeightA || !baggageWeightB) {
      return null;
    }

    return baggageWeightA < baggageWeightB ? a : b;
  });
  return minBaggageWeightForSection ? minBaggageWeightForSection.transport.baggage : {};
}

function formatConnectionLabel(label: ?string, duration: ?string): string {
  const durationAsNumber = duration ? +duration : 0;
  const durationFormated = moment.utc(
    moment.duration(durationAsNumber, 'minutes').asMilliseconds(),
  );
  return messageFormat('{label} {duration}').format({
    label,
    duration: `${durationFormated.format('HH')}h${durationFormated.format('mm')}`,
  });
}

function getStep(i18n: CMSContent): ({}) => {} {
  const { timeFormat } = i18n.common;
  const transportConnectionLabel = i18n.common.transport.connection;

  return step => {
    const connectionDuration = get(step, 'connectionDurationInMin');
    const stopOverDuration = get(step, 'stopDurationInMin');

    const information =
      !isEmpty(stopOverDuration) || !isEmpty(connectionDuration)
        ? formatConnectionLabel(transportConnectionLabel, stopOverDuration || connectionDuration)
        : null;

    return {
      id: get(step, 'transport.reference'),
      type: get(step, 'transport.type'),
      information,
      operator: get(step, 'transport.companyOperator.label'),
      operatorId: get(step, 'transport.companyOperator.id'),
      operatorLogo: get(step, 'transport.companyOperator.logo'),
      start: {
        label: get(step, 'departure.airportLabel'),
        time: formatTime(get(step, 'departure.time'), timeFormat),
        date: get(step, 'departure.date'),
      },
      end: {
        label: get(step, 'arrival.airportLabel'),
        time: formatTime(get(step, 'arrival.time'), timeFormat),
        date: get(step, 'arrival.date'),
      },
      technicalStops: get(step, 'technicalStops'),
    };
  };
}

function hasBoat(travelSections: ?TravelSections) {
  if (!travelSections || !travelSections.length) {
    return false;
  }

  return travelSections.every(section => get(section, 'transport.type') === BOAT);
}

function hasTrain(travelSections: ?TravelSections) {
  if (!travelSections || !travelSections.length) {
    return false;
  }

  return travelSections.some(section => get(section, 'transport.type') === TRAIN);
}

function hasOnlyPlanes(inboundFlight: ?TransportDetail, outboundFlight: ?TransportDetail) {
  return !(
    hasTrain(get(inboundFlight, 'travelSections')) ||
    hasTrain(get(outboundFlight, 'travelSections')) ||
    hasBoat(get(inboundFlight, 'travelSections')) ||
    hasBoat(get(outboundFlight, 'travelSections'))
  );
}

function getTransportIcon(
  inboundFlight: ?TransportDetail,
  outboundFlight: ?TransportDetail,
): string {
  const isTrain =
    hasTrain(get(inboundFlight, 'travelSections')) &&
    hasTrain(get(outboundFlight, 'travelSections'));
  if (isTrain) {
    return ICONS.TRAIN;
  }
  const isBoat =
    hasBoat(get(inboundFlight, 'travelSections')) && hasBoat(get(outboundFlight, 'travelSections'));
  if (isBoat) {
    return ICONS.BOAT;
  }
  return ICONS.TRANSPORT;
}

function tripHasSchedules(travelSections: ?TravelSections): boolean {
  return !isEmpty(travelSections)
    ? (travelSections || []).some(
        section => get(section, 'arrival.time') || get(section, 'departure.time'),
      )
    : false;
}

function tripsHaveSchedules(
  inboundFlight: ?TransportDetail,
  outboundFlight: ?TransportDetail,
): boolean {
  return (
    tripHasSchedules(get(inboundFlight, 'travelSections')) ||
    tripHasSchedules(get(outboundFlight, 'travelSections'))
  );
}

function transformMinutesInHours(minutes: string): string {
  const numberMinutes = Number(minutes);

  return Math.floor(numberMinutes / 60) + 'h' + (numberMinutes % 60).toString().padStart(2, '0');
}

function hasCancellationPolicy(transport: Transport): ?boolean {
  return (
    isCharterFlight(get(transport, 'inbound.cancellationPolicyType')) ||
    isCharterFlight(get(transport, 'outbound.cancellationPolicyType'))
  );
}

const getCancellationPolicyDescription = (transport: Transport, i18n: CMSContent): ?string => {
  if (hasCancellationPolicy(transport)) {
    return i18n.bookingProcess.find(
      legalInformation =>
        legalInformation.id ===
        (transport.inbound.cancellationPolicyType || transport.outbound.cancellationPolicyType),
    )?.description;
  }
};

export const getFindSectionTransportTypeFn = (transportType: string) => ({
  transport: { type },
}: TravelSection) => type === transportType;

export {
  getDiffAbsoluteDays,
  formatTime,
  getWayLabel,
  getMinBaggageWeight,
  formatConnectionLabel,
  getStep,
  hasOnlyPlanes,
  tripsHaveSchedules,
  transformMinutesInHours,
  getTransportIcon,
  hasCancellationPolicy,
  getCancellationPolicyDescription,
};
